import { useMemo } from 'react';
import { capitalizeFirstLetter } from '@einhaus/meteoric-helpers';
import { usePortfolios } from '@/hooks/usePortfolios';
import SearchSelect, { type SearchSelectFormProps, type SearchSelectItem } from './SearchSelect';

type Portfolio = ReturnType<typeof usePortfolios>['portfolios'][number];

export interface PortfolioSelectProps extends SearchSelectFormProps {
    value: number | null;
    onChange: (portfolioId: number | null) => void;
    /** Narrows the selectable list, e.g. to paper or live portfolios */
    filter?: (portfolio: Portfolio) => boolean;
}

const toItem = (portfolio: Portfolio): SearchSelectItem => {
    const name = portfolio.name || `Portfolio ${portfolio.id}`;

    return {
        label: `${name} (#${portfolio.id})`,
        meta: portfolio.type ? capitalizeFirstLetter(portfolio.type) : undefined,
        name,
        value: portfolio.id
    };
};

const missingItem = (portfolioId: number): SearchSelectItem => ({
    label: `Portfolio ${portfolioId} (#${portfolioId})`,
    meta: 'Not in your current portfolios',
    name: `Portfolio #${portfolioId}`,
    value: portfolioId
});

/**
 * Searchable single select for the user's portfolios. Fetches its own data via
 * usePortfolios, so call sites only manage the selected id. Search matches name
 * and id (with or without #).
 */
const PortfolioSelect = (props: PortfolioSelectProps) => {
    const { value, onChange, filter, placeholder, ...formProps } = props;

    const { error: portfoliosError, isLoading, portfolios } = usePortfolios();

    const items = useMemo(() => {
        const visible = filter ? portfolios.filter(filter) : portfolios;

        return visible.map(toItem);
    }, [portfolios, filter]);

    const loadError = portfoliosError ? `Failed to load portfolios: ${portfoliosError.message}` : undefined;

    return (
        <SearchSelect
            {...formProps}
            isLoading={isLoading}
            items={items}
            loadError={loadError}
            mode="single"
            placeholder={placeholder ?? 'Select a portfolio...'}
            resolveMissingItem={missingItem}
            value={value}
            onChange={onChange}
        />
    );
};

export default PortfolioSelect;
